import ApiManager from './api/ApiManager';

/**
 * Freight AI Service
 * Handles communication with backend AI chat endpoint
 */
class FreightAIService {
  constructor() {
    this.chatHistory = [];
  }

  /**
   * Send a message to the AI and get a response
   * @param {string} message - User's message
   * @param {Array} chatHistory - Previous chat messages
   * @returns {Promise<Object>} - AI response with message and any tool calls
   */
  async sendMessage(message, chatHistory = []) {
    try {
      console.log('[FreightAI] Sending message to backend:', message);
      
      // Call backend freight-ai endpoint
      const response = await ApiManager.request('analytics', 'POST', '/freight-ai', {
        message: message,
        chat_history: chatHistory
      });

      console.log('[FreightAI] Full response from backend:', response);
      
      if (response.status === 'success' && response.data) {
        console.log('[FreightAI] Received successful response:', response.data);
        
        // Format response for frontend
        return {
          message: response.data.message,
          toolCalls: response.data.tool_calls || null,
          hasToolCalls: response.data.has_tool_calls || false,
          success: true
        };
      } else {
        console.error('[FreightAI] Backend returned error or invalid response:', { 
          status: response.status, 
          data: response.data, 
          error: response.error,
          fullResponse: response
        });
        return {
          message: response.error || response.data?.error || 'Failed to get AI response',
          success: false
        };
      }
    } catch (error) {
      console.error('[FreightAI] Error calling backend:', error);
      
      // Check if it's an authentication error
      if (error.response?.status === 401) {
        return {
          message: 'Please sign in to use Freight AI chat',
          success: false
        };
      }
      
      // Check if it's a rate limit error
      if (error.response?.status === 429) {
        return {
          message: 'Rate limit exceeded. Please wait a moment before trying again.',
          success: false
        };
      }
      
      return {
        message: 'Sorry, I encountered an error. Please try again.',
        success: false
      };
    }
  }

  /**
   * Format tool results for display
   * @param {Array} toolCalls - Tool call results from backend
   * @returns {Object} - Formatted display data
   */
  formatToolResults(toolCalls) {
    if (!toolCalls || !Array.isArray(toolCalls)) {
      return null;
    }

    const formatted = {};

    for (const call of toolCalls) {
      const { function_name, result } = call;

      if (function_name === 'search_loads' && result?.loads) {
        // execute_search_loads() already returns flattened, formatted loads
        formatted.loads = result.loads.slice(0, 10).map(load => ({
          origin: load.origin,
          destination: load.destination,
          payment: `$${(load.payment || 0).toLocaleString()}`,
          rate: `$${(load.rate_per_mile || 0).toFixed(2)}/mi`,
          miles: `${load.mileage || 0} mi`,
          equipment: load.equipment || 'Van'
        }));
        formatted.totalLoads = result.total_found ?? result.loads.length;
      } else if (function_name === 'get_lane_pricing' && result) {
        if (result.error) {
          formatted.pricing = { error: result.error };
        } else if (result.summary && typeof result.summary === 'object') {
          formatted.pricing = {
            totalLoads: result.summary.total_loads,
            avgRatePerMile: result.summary.average_rate_per_mile,
            avgPayment: result.summary.average_payment,
            equipment: result.summary.equipment,
            searchRadius: result.summary.search_radius
          };
          formatted.loads = (result.loads || []).slice(0, 10).map(load => ({
            origin: load.origin,
            destination: load.destination,
            payment: `$${(load.payment || 0).toLocaleString()}`,
            rate: `$${(load.rate_per_mile || 0).toFixed(2)}/mi`,
            miles: `${load.mileage || 0} mi`,
            equipment: load.equipment || 'Van'
          }));
        } else {
          formatted.pricing = { error: result.summary || 'No matching loads found' };
        }
      } else if (function_name === 'get_rate_insights' && result?.insights) {
        formatted.insights = {
          totalLoads: result.insights.total_records,
          avgRatePerMile: result.insights.average_rate_per_mile,
          avgPayment: result.insights.average_payment,
          avgMileage: result.insights.average_mileage,
          topLanes: result.top_lanes
        };
      } else if (function_name === 'get_broker_metrics' && result?.brokers) {
        formatted.brokers = result.brokers.map(b => ({
          name: b.broker_name,
          totalRevenue: `$${(b.total_revenue || 0).toLocaleString()}`,
          avgPayment: `$${(b.avg_payment || 0).toLocaleString()}`,
          totalLoads: b.total_loads,
          avgRate: `$${(b.avg_rate || 0).toFixed(2)}/mi`,
          uniqueLanes: b.unique_lanes
        }));
      }
    }
    
    return Object.keys(formatted).length > 0 ? formatted : null;
  }

  /**
   * Clear chat history
   */
  clearHistory() {
    this.chatHistory = [];
  }
}

// Export singleton instance
const freightAIService = new FreightAIService();
export default freightAIService;